import React, { useMemo } from 'react'
import { ICSActionIcon } from '../../../components'
import { ResultEntity } from '../../../types/results'
import {
  IPA_RESULT_ACTIONS,
  IPAResultAction
} from '../../../services/endpoints'
import { LockIcon, UnlockIcon } from './ResultsActionIcons'

type AdminActionConfig = {
  key: string
  icon?: string
  customIcon?: 'lock' | 'unlock'
  title: string
  action: IPAResultAction
  disabled?: boolean
}

type ResultsActionCellProps = {
  data: ResultEntity
  canManageResults: boolean
  onDelete: (result: ResultEntity) => void | Promise<void>
  onResultAction: (
    result: ResultEntity,
    action: IPAResultAction
  ) => void | Promise<void>
}

const getAdminActionsForResult = (
  result: ResultEntity
): AdminActionConfig[] => {
  const isRecycled = Boolean(result.recycled)
  const isLocked = Boolean(result.retention_lock)

  return [
    {
      key: isRecycled ? 'restore' : 'recycle',
      icon: isRecycled ? 'revert' : 'refresh',
      title: isLocked
        ? isRecycled
          ? 'Restore Result (disabled while locked)'
          : 'Recycle Result (disabled while locked)'
        : isRecycled
        ? 'Restore Result'
        : 'Recycle Result',
      action: isRecycled
        ? IPA_RESULT_ACTIONS.RESTORE
        : IPA_RESULT_ACTIONS.RECYCLE,
      disabled: isLocked
    },
    {
      key: isLocked ? 'lock' : 'unlock',
      customIcon: isLocked ? 'lock' : 'unlock',
      title: isRecycled
        ? `${
            isLocked ? 'Unlock' : 'Lock'
          } Retention (disabled for recycled results)`
        : isLocked
        ? 'Unlock Retention'
        : 'Lock Retention',
      action: isLocked ? IPA_RESULT_ACTIONS.UNLOCK : IPA_RESULT_ACTIONS.LOCK,
      disabled: isRecycled
    }
  ]
}

const ResultsActionCell: React.FC<ResultsActionCellProps> = ({
  data,
  canManageResults,
  onDelete,
  onResultAction
}) => {
  const isLocked = Boolean(data.retention_lock)

  const adminActions = useMemo(
    () => (canManageResults ? getAdminActionsForResult(data) : []),
    [canManageResults, data]
  )

  return (
    <div className="results-action-group">
      <ICSActionIcon
        icon="trash"
        title={
          isLocked ? 'Delete Result (disabled while locked)' : 'Delete Result'
        }
        renderData={{ data }}
        disabled={isLocked}
        onClick={isLocked ? undefined : () => onDelete(data)}
      />
      {adminActions.map((actionConfig) =>
        actionConfig.customIcon ? (
          <button
            key={`${actionConfig.key}_${data.id}`}
            type="button"
            className={`results-action-icon-button ics-action-icon ${
              actionConfig.disabled ? 'disabled' : ''
            }`}
            title={actionConfig.title}
            aria-label={actionConfig.title}
            disabled={actionConfig.disabled}
            onClick={() => onResultAction(data, actionConfig.action)}
          >
            {actionConfig.customIcon === 'lock' ? <LockIcon /> : <UnlockIcon />}
          </button>
        ) : (
          <ICSActionIcon
            key={`${actionConfig.key}_${data.id}`}
            icon={actionConfig.icon as string}
            title={actionConfig.title}
            renderData={{ data }}
            disabled={actionConfig.disabled}
            onClick={
              actionConfig.disabled
                ? undefined
                : () => onResultAction(data, actionConfig.action)
            }
          />
        )
      )}
    </div>
  )
}

export default React.memo(ResultsActionCell)
